import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isPortalUser, goToPortal } from '../utils/portalLink';

const BANNER_HEIGHT = 44;

export default function KycAlertBanner() {
    const [visible, setVisible] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const location = useLocation();
    const styleTagRef = useRef<HTMLStyleElement | null>(null);

    const onDashboard = location.pathname.startsWith('/tenant/dashboard');
    const active = visible && !dismissed && !onDashboard;

    const checkKycNeeded = () => {
        try {
            const stored = localStorage.getItem('srs_user');
            if (!stored) { setVisible(false); setCurrentUser(null); return; }
            const user = JSON.parse(stored);
            setCurrentUser(user);
            setVisible(!!user && user.kyc_verified !== true);
        } catch {
            setVisible(false);
            setCurrentUser(null);
        }
    };

    useEffect(() => {
        checkKycNeeded();
        window.addEventListener('userUpdated', checkKycNeeded);
        window.addEventListener('storage', checkKycNeeded);
        return () => {
            window.removeEventListener('userUpdated', checkKycNeeded);
            window.removeEventListener('storage', checkKycNeeded);
        };
    }, []);

    // Re-check on route change
    useEffect(() => {
        checkKycNeeded();
    }, [location.pathname]);

    // Inject / remove a <style> tag that nudges the fixed navbar down
    useEffect(() => {
        if (active) {
            if (!styleTagRef.current) {
                const tag = document.createElement('style');
                tag.id = 'kyc-banner-style';
                tag.textContent = `
                    /* Push fixed header below the KYC banner */
                    .header.header-fixed {
                        top: ${BANNER_HEIGHT}px !important;
                    }
                    /* Push transparent header (home page) below the KYC banner */
                    .header.header-transparent:not(.header-fixed) {
                        margin-top: ${BANNER_HEIGHT}px;
                    }
                `;
                document.head.appendChild(tag);
                styleTagRef.current = tag;
            }
        } else {
            if (styleTagRef.current) {
                styleTagRef.current.remove();
                styleTagRef.current = null;
            }
        }

        return () => {
            if (styleTagRef.current) {
                styleTagRef.current.remove();
                styleTagRef.current = null;
            }
        };
    }, [active]);

    if (!active) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10000, // above the navbar's z-index: 999
            background: 'linear-gradient(90deg, #1D0A3B 0%, #3b1f7a 50%, #1D0A3B 100%)',
            borderBottom: '2px solid #FABD23',
            height: BANNER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: `0 48px 0 16px`,
        }}>
            {/* Icon */}
            <i className="fa-solid fa-shield-halved" style={{ color: '#FABD23', fontSize: 14, flexShrink: 0 }} />

            {/* Static centered text */}
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>
                Your identity is not verified yet —
                <span style={{ color: '#FABD23', fontWeight: 700, marginLeft: 4 }}>
                    complete KYC to unlock property requests, viewings &amp; direct chat.
                </span>
            </span>

            {/* CTA button — portal users go to portal /documents, tenants stay on site */}
            {isPortalUser(currentUser) ? (
                <button
                    onClick={() => goToPortal('/documents')}
                    style={{
                        flexShrink: 0,
                        padding: '5px 14px',
                        background: '#FABD23',
                        color: '#1D0A3B',
                        borderRadius: 6,
                        fontWeight: 700,
                        fontSize: 12,
                        border: 'none',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <i className="fa-solid fa-arrow-right" style={{ fontSize: 11 }} />
                    Verify Now
                </button>
            ) : (
                <Link
                    to="/tenant/dashboard?tab=profile"
                    style={{
                        flexShrink: 0,
                        padding: '5px 14px',
                        background: '#FABD23',
                        color: '#1D0A3B',
                        borderRadius: 6,
                        fontWeight: 700,
                        fontSize: 12,
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <i className="fa-solid fa-arrow-right" style={{ fontSize: 11 }} />
                    Verify Now
                </Link>
            )}

            {/* Dismiss */}
            <button
                onClick={() => setDismissed(true)}
                style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'rgba(255,255,255,0.55)',
                    cursor: 'pointer',
                    fontSize: 18,
                    lineHeight: 1,
                    padding: '0 4px',
                }}
                aria-label="Dismiss"
                title="Dismiss for this session"
            >
                ×
            </button>
        </div>
    );
}
